/**
 * หน้าเจ้าของเท่านั้น (เห็นเมื่อ HR/MANAGER เปิดลิงก์ที่ปลายทางเป็น owner-only)
 *
 * เดิม route พวกนี้ลงทะเบียนเฉพาะ OWNER คนอื่นกดมาถึงจะตกไป `*` แล้วเด้งกลับแดชบอร์ดเงียบ ๆ
 * ลูกค้าไม่รู้ว่าเพราะสิทธิ์หรือระบบพัง (TN Riches 20 ก.ย. 2569) — บอกให้ชัดว่าใครแก้ได้ และ
 * ส่วนไหนที่ฝ่ายบุคคลแก้เองได้อยู่ที่ไหน
 *
 * fb#176 (TN Riches 2 ต.ค. 2569): ข้อมูลบริษัท (ชื่อ/เลขผู้เสียภาษี/ผู้ลงนาม) ฝ่ายบุคคลที่มีสิทธิ์
 * `edit_company` กรอกเองได้ที่นี่ — ส่วนอื่นของหน้าตั้งค่า (ความปลอดภัย AI สำรองข้อมูล) ยังเป็นของเจ้าของ
 */
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { CompanyProfileForm } from '../../components/CompanyProfileForm';
import { useMyCapabilities } from '../../hooks';
import { useAuthStore } from '../../lib/store';

export function OwnerOnlyPage() {
  const loc = useLocation();
  const role = useAuthStore((s) => s.profile?.role);
  const caps = useMyCapabilities();
  const canEditCompany = role === 'HR' && (caps.data ?? []).includes('edit_company');
  // ปลายทางคือข้อมูลบริษัท (ลิงก์จากพิมพ์เขียว/เมนู) และ HR มีสิทธิ์ → ให้ฟอร์มเลย ไม่ต้องอธิบายว่าทำไมไม่ได้
  const wantsCompany = loc.pathname === '/owner/settings' && (loc.hash === '' || loc.hash === '#company' || loc.hash === '#overview');

  if (loc.pathname === '/owner/settings' && role === 'HR' && caps.isLoading) {
    return (
      <AdminLayout title="ตั้งค่าระบบ">
        <Loading />
      </AdminLayout>
    );
  }

  if (wantsCompany && canEditCompany) {
    return (
      <AdminLayout title="ข้อมูลบริษัท">
        <div className="max-w-xl mx-auto space-y-3">
          <CompanyProfileForm />
          <p className="text-xs text-gray-500">
            ส่วนอื่นของการตั้งค่าระบบ (ความปลอดภัย · AI · สำรองข้อมูล · โหมดอนุมัติเงินเดือน) แก้ได้เฉพาะบัญชีเจ้าของระบบ ·{' '}
            <Link to="/owner/blueprint" className="text-teal hover:underline">กลับไปพิมพ์เขียว</Link>
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="ตั้งค่าระบบ">
      <div className="card p-6 max-w-xl mx-auto text-sm">
        <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
          <Lock className="w-4 h-4 text-amber-500 shrink-0" />
          หน้านี้แก้ได้เฉพาะบัญชีเจ้าของระบบ
        </div>
        <p className="text-gray-600">
          ความปลอดภัย AI สำรองข้อมูล และโหมดอนุมัติเงินเดือน ต้องให้เจ้าของระบบเป็นคนตั้ง
        </p>
        {canEditCompany ? (
          <p className="text-gray-600 mt-2">
            ข้อมูลบริษัท (ชื่อ · เลขผู้เสียภาษี · ผู้ลงนามในสลิป) คุณแก้ได้ที่{' '}
            <Link to="/owner/settings#company" className="text-teal hover:underline">ข้อมูลบริษัท</Link>
          </p>
        ) : (
          <p className="text-gray-600 mt-2">
            ข้อมูลบริษัท (ชื่อ · เลขผู้เสียภาษี · ผู้ลงนามในสลิป) ฝ่ายบุคคลกรอกเองได้เมื่อเจ้าของเปิดสิทธิ์
            “ตั้งค่าบริษัท (โปรไฟล์)” ให้ที่เมนู ตั้งค่าสิทธิ์ HR — ตอนนี้บัญชีของคุณยังไม่มีสิทธิ์นี้
          </p>
        )}
        <p className="text-gray-600 mt-2">
          ส่วนที่ฝ่ายบุคคลตั้งเองได้: <Link to="/owner/wage#paycycle" className="text-teal hover:underline">รอบจ่ายเงินเดือน</Link> ·{' '}
          <Link to="/hr/time#settings" className="text-teal hover:underline">ตั้งค่าเวลา</Link> ·{' '}
          <Link to="/owner/wage#allowance" className="text-teal hover:underline">เงินเพิ่ม</Link>
        </p>
        <Link to="/owner/blueprint" className="btn-secondary inline-flex mt-4">กลับไปพิมพ์เขียว</Link>
      </div>
    </AdminLayout>
  );
}
